import { configService } from './configService';

// Backend API service per le registrazioni
export interface BackendRegistrationResponse {
  success: boolean;
  message: string;
  data?: {
    id: number;
    timestamp: string;
    email: string;
    numeroPartecipanti: number;
    bookingNumber?: string;
    emailSent?: boolean;
  };
  errors?: any[];
  error?: string;
  registrationsClosed?: boolean;
  attemptId?: string;
}

export interface RegistrationStatusResponse {
  open: boolean;
  message: string;
}

export class BackendApiService {
  private static instance: BackendApiService;
  private baseUrl: string;

  private constructor() {
    // URL del backend - usando il servizio di configurazione centralizzato
    this.baseUrl = configService.getBackendUrl();
  }

  public static getInstance(): BackendApiService {
    if (!BackendApiService.instance) {
      BackendApiService.instance = new BackendApiService();
    }
    return BackendApiService.instance;
  }

  // Invia registrazione al backend
  async submitRegistration(registrationData: any): Promise<BackendRegistrationResponse> {
    console.log('🚀 [REG-FLOW] Invio registrazione al backend...', {
      url: `${this.baseUrl}/api/registration`,
      email: registrationData?.email,
      hasPayment: !!registrationData?.paymentDetails,
      paymentOrderId: registrationData?.paymentDetails?.orderId
    });

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/api/registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      });
    } catch (networkError) {
      console.error('❌ [REG-FLOW] Errore di rete (fetch fallita):', networkError);
      return {
        success: false,
        message: 'Impossibile connettersi al server. Verifica la connessione internet e riprova.'
      };
    }

    let data: any;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('❌ [REG-FLOW] Risposta del server non leggibile come JSON:', parseError, 'HTTP status:', response.status);
      return {
        success: false,
        message: `Il server ha risposto con stato ${response.status} ma senza un corpo leggibile. Se hai già completato un pagamento, contatta l'assistenza indicando data e ora esatte.`
      };
    }

    if (!response.ok) {
      // IMPORTANTE: non scartare mai il payload del backend qui. Contiene il motivo
      // specifico del rifiuto (data.errors) e l'attemptId per ritrovare i log lato server.
      console.error('❌ [REG-FLOW] Il backend ha rifiutato la registrazione:', data);
      return { success: false, ...data };
    }

    console.log('✅ [REG-FLOW] Registrazione salvata con successo:', data);
    return data;
  }

  // Segnalazione di emergenza: usata quando submitRegistration fallisce dopo un pagamento
  // già riuscito, per non perdere le prove della transazione. Non deve mai bloccare la UI:
  // eventuali errori vengono solo loggati, mai propagati al chiamante.
  async reportRegistrationIssue(payload: any): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/api/registration/report-issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      console.log('🚨 [REG-FLOW] Segnalazione di emergenza inviata al backend');
    } catch (error) {
      console.error('❌ [REG-FLOW] Impossibile inviare la segnalazione di emergenza:', error);
    }
  }

  // Test connessione backend
  async testConnection(): Promise<BackendRegistrationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/registration/test`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Test connessione fallito');
      }

      return data;
    } catch (error) {
      console.error('❌ Test connection failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Test connessione fallito'
      };
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return await response.json();
    } catch (error) {
      throw new Error('Backend non raggiungibile');
    }
  }

  // Controlla se le iscrizioni sono aperte
  async checkRegistrationStatus(): Promise<RegistrationStatusResponse> {
    try {
      console.log('🔍 Checking registration status...');
      
      const response = await fetch(`${this.baseUrl}/api/registration/status`);
      const data = await response.json();

      console.log('📊 Registration status:', data);
      return data;
    } catch (error) {
      console.error('❌ Error checking registration status:', error);
      // In caso di errore, assumiamo che le iscrizioni siano aperte
      return {
        open: true,
        message: 'Stato iscrizioni non verificabile, procedendo...'
      };
    }
  }

  // Getter per l'URL del backend (per debug)
  getBackendUrlForDebug(): string {
    return this.baseUrl;
  }
}

// Esporta istanza singleton
export const backendApiService = BackendApiService.getInstance();
